/**
 * Child half of the iframe auto-resize handshake.
 *
 * Posts the document's content height to the parent page whenever it
 * changes (step navigation, conditional fields, validation errors, the
 * success panel). The parent half lives in public/embed.js.
 *
 * The message carries no sensitive data, so it is posted with '*' as the
 * target origin — the embedding page is not knowable in advance (prod +
 * staging hosts). The security-relevant validation happens on the PARENT
 * side, which accepts resize messages only from this form's exact origin.
 */

const MESSAGE_TYPE = 'kidneycars:resize'

let lastHeight = 0
let rafId = 0

function measure(): number {
  // body height (html/body have no margins in our CSS) — unlike
  // documentElement.scrollHeight, it shrinks when content shrinks.
  return Math.ceil(document.body.getBoundingClientRect().height)
}

function post(): void {
  const height = measure()
  if (height > 0 && height !== lastHeight) {
    lastHeight = height
    window.parent.postMessage({ type: MESSAGE_TYPE, height }, '*')
  }
}

function schedule(): void {
  cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(post)
}

export function initResizeChild(): void {
  if (window.parent === window) return // not embedded — nothing to do

  new ResizeObserver(schedule).observe(document.body)

  // DOM mutations that don't change body box size immediately (e.g. async
  // widget/iframe injection) — cheap to observe, debounced by rAF anyway.
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
  })

  window.addEventListener('resize', schedule)

  // Initial posts: now, and again once fonts settle layout.
  post()
  document.fonts?.ready.then(schedule).catch(() => {})
}
